import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import RealtimeJobAgentPanel from '../components/ai/RealtimeJobAgentPanel';
import JobProtocolButton from '../components/job-protocol/JobProtocolButton';
import { createCustomer, deleteJobById, fetchCustomers, fetchJobById, saveJob } from '../lib/crmClient';

const todayIso = () => new Date().toISOString().slice(0, 10);

const FIRMY = [
  { id: '1', name: 'Derator s.r.o.' },
  { id: '2', name: 'Tomáš Raška - DDD služby' },
  { id: '3', name: 'Derabyt s.r.o.' },
  { id: '4', name: 'Deratizace Raška s.r.o.' },
];

const ZAMESTNANCI = [
  { id: '1', fullName: 'Tomáš Raška', email: 'tomas.raska@derator.cz', kmRate: 5.5 },
  { id: '1007', fullName: 'Pavel Sedláček', email: 'pavel.sedlacek@derator.cz', kmRate: 1.0 },
  { id: '1011', fullName: 'Filip Landovský', email: 'filip.landovsky@derator.cz', kmRate: 5.5 },
  { id: '1012', fullName: 'Libor Landovský', email: 'libor.landovsky@derator.cz', kmRate: 5.5 },
  { id: '1013', fullName: 'Jaromír Mareš', email: 'jaromir.mares@derator.cz', kmRate: 5.5 },
  { id: '1027', fullName: 'Petr Sedláček', email: 'petr.sedlacek@derator.cz', kmRate: 5.5 },
  { id: '1042', fullName: 'Radek Steppan', email: 'radek.steppan@derator.cz', kmRate: 5.5 },
  { id: '1044', fullName: 'Adam Duda', email: 'adam.duda@derator.cz', kmRate: 5.5 },
  { id: '1048', fullName: 'Vladimír Nedopil', email: 'vladimir.nedopil@derator.cz', kmRate: 5.5 },
  { id: '1050', fullName: 'Lukáš Franěk', email: 'lukas.franek@derator.cz', kmRate: 5.5 },
];

const ZPUSOBY_PLATBY = [
  { id: '1', label: 'Hotově' },
  { id: '2', label: 'Faktura (Převodem)' },
];

const SKUDCI = [
  { id: '1', label: 'Deratizace' },
  { id: '2', label: 'Dezinsekce' },
  { id: '3', label: 'Dezinfekce' },
  { id: '4', label: 'Myš, potkan, krysa' },
  { id: '5', label: 'Rus, Šváb' },
  { id: '6', label: 'Štěnice' },
  { id: '7', label: 'Vosy, včely, sršni' },
  { id: '8', label: 'Blechy' },
  { id: '9', label: 'Mouhy, pavouci' },
  { id: '10', label: 'Ostatní' },
  { id: '11', label: 'Mravenci' },
  { id: '12', label: 'Fumigace' },
];

const DEFAULT_NEW_JOB_STATUS_ID = '4';
const DEFAULT_FAKTURA_STATUS_ID = '1';

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const normalizeReturnTo = (value) => {
  const fallback = { pathname: '/zakazky' };
  if (!value) return fallback;

  if (typeof value === 'string') {
    return value.startsWith('/') ? { pathname: value } : fallback;
  }

  if (typeof value === 'object' && typeof value.pathname === 'string' && value.pathname.startsWith('/')) {
    return {
      pathname: value.pathname,
      search: value.search || '',
      state: value.state || undefined,
    };
  }

  return fallback;
};

const returnLabel = (returnTo) => (
  returnTo && returnTo.pathname === '/provize' ? 'Zpět na provize' : 'Zpět na přehled'
);

const getEmployeeIdByEmail = (userEmail) => {
  const match = ZAMESTNANCI.find((item) => normalizeEmail(item.email) === normalizeEmail(userEmail));
  return match ? match.id : '';
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = String(value).replace(',', '.').trim();
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const toIntegerOrNull = (value) => {
  const n = toNumberOrNull(value);
  return n === null ? null : Math.round(n);
};

const normalizeWholeInputValue = (value) => {
  const rounded = toIntegerOrNull(value);
  return rounded === null ? '' : String(rounded);
};

const emptyCustomer = {
  Nazev: '',
  Ulice: '',
  KontaktniOsoba: '',
  Telefon: '',
};

const buildSavedJobPreview = ({
  response,
  form,
  customer,
  effectiveEmployeeId,
  effectiveStatusId,
  effectivePlannedDate,
  email,
}) => {
  const responseJobId = Number(response && response.ZakazkaId ? response.ZakazkaId : 0);
  const formJobId = Number(form && form.ZakazkaId ? form.ZakazkaId : 0);
  const zakazkaId = responseJobId || formJobId || null;

  if (!zakazkaId) return null;

  return {
    ZakazkaId: zakazkaId,
    Datum: form.Datum,
    DatumNaplanovano: effectivePlannedDate || null,
    ZakaznikId: Number(form.ZakaznikId),
    NazevZakazky: (customer?.Nazev || form.NazevZakazky || '').trim() || null,
    NazevZakaznika: (customer?.Nazev || '').trim() || null,
    SkudceId: Number(form.SkudceId) || null,
    ZamestnanecId: Number(effectiveEmployeeId) || null,
    TechnikEmail: email || null,
    CenaZakazky: toIntegerOrNull(form.CenaZakazky),
    Doprava: toIntegerOrNull(form.DopravaKm),
    ZpusobPlatbyId: Number(form.ZpusobPlatbyId) || null,
    StatusId: Number(effectiveStatusId) || Number(DEFAULT_NEW_JOB_STATUS_ID),
    Status: null,
    Popis: form.Poznamka.trim() || null,
    ProtokolBlobUrl: form.ProtokolBlobUrl || null,
  };
};

const buildSavedJobPreviewFromDraft = ({ response, draft, effectiveEmployeeId, email }) => {
  const zakazkaId = Number(response && response.ZakazkaId ? response.ZakazkaId : 0);
  if (!zakazkaId) return null;

  return {
    ZakazkaId: zakazkaId,
    Datum: draft && draft.Datum ? String(draft.Datum) : todayIso(),
    DatumNaplanovano: null,
    ZakaznikId: Number(draft && draft.ZakaznikId ? draft.ZakaznikId : 0),
    NazevZakazky: String((draft && draft.ZakaznikNazev) || '').trim() || null,
    NazevZakaznika: String((draft && draft.ZakaznikNazev) || '').trim() || null,
    SkudceId: Number(draft && draft.SkudceId ? draft.SkudceId : 0) || null,
    ZamestnanecId: Number(effectiveEmployeeId) || null,
    TechnikEmail: email || null,
    CenaZakazky: toIntegerOrNull(draft && draft.CenaZakazky),
    Doprava: toIntegerOrNull(draft && draft.DopravaKm),
    ZpusobPlatbyId: Number(draft && draft.ZpusobPlatbyId ? draft.ZpusobPlatbyId : 0) || null,
    StatusId: Number(DEFAULT_NEW_JOB_STATUS_ID),
    Status: null,
    Popis: String((draft && draft.Poznamka) || '').trim() || null,
  };
};

const ZakazkaForm = ({ email }) => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const returnTo = useMemo(() => normalizeReturnTo(location.state && location.state.returnTo), [location.state]);

  const copyFromId = useMemo(() => {
    const query = new URLSearchParams(location.search || '');
    return String(query.get('copyFrom') || '').trim();
  }, [location.search]);

  const isCopyMode = !isEdit && Number(copyFromId) > 0;

  const [form, setForm] = useState({
    ZakazkaId: null,
    NazevZakazky: '',
    ZakaznikId: 0,
    Datum: todayIso(),
    ZamestnanecId: '',
    FirmaId: '',
    Poznamka: '',
    DatumNaplanovano: '',
    CenaZakazky: '',
    OstatniNaklady: '',
    DopravaKm: '',
    CisloProtokolu: '',
    ZpusobPlatbyId: '2',
    SkudceId: '10',
    StatusId: DEFAULT_NEW_JOB_STATUS_ID,
    FakturaStatusId: DEFAULT_FAKTURA_STATUS_ID,
  });
  const [customers, setCustomers] = useState([]);
  const [customerForm, setCustomerForm] = useState(emptyCustomer);
  const [customerSearch, setCustomerSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState('list');
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const [showMoreMobile, setShowMoreMobile] = useState(false);
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [voiceSeed, setVoiceSeed] = useState({});
  const [voiceSaved, setVoiceSaved] = useState(false);
  const [voicePending, setVoicePending] = useState(() => {
    try { return !isEdit && Boolean(sessionStorage.getItem(`crm:voice-session:${String(email || '').toLowerCase()}`)); } catch { return false; }
  });

  const defaultEmployeeId = useMemo(() => getEmployeeIdByEmail(email), [email]);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)');
    const sync = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }

    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  useEffect(() => {
    const run = async () => {
      setError('');
      try {
        const customerList = await fetchCustomers();
        setCustomers(customerList);

        if (isEdit || isCopyMode) {
          const sourceId = isEdit ? id : copyFromId;
          const item = await fetchJobById({ email, zakazkaId: sourceId });
          if (!item) throw new Error('Zdrojová zakázka nebyla nalezena.');

          if (isEdit) {
            setForm({
              ZakazkaId: item.ZakazkaId,
              ProtokolBlobUrl: item.ProtokolBlobUrl || null,
              NazevZakazky: item.NazevZakazky || '',
              ZakaznikId: item.ZakaznikId || 0,
              Datum: item.Datum || todayIso(),
              ZamestnanecId: item.ZamestnanecId ? String(item.ZamestnanecId) : (defaultEmployeeId || ''),
              FirmaId: item.FirmaId ? String(item.FirmaId) : '1',
              Poznamka: item.Poznamka || item.Popis || '',
              DatumNaplanovano: item.DatumNaplanovano || '',
              CenaZakazky: item.CenaZakazky !== null && item.CenaZakazky !== undefined ? String(Math.round(Number(item.CenaZakazky) || 0)) : '',
              OstatniNaklady: item.OstatniNaklady !== null && item.OstatniNaklady !== undefined ? String(Math.round(Number(item.OstatniNaklady) || 0)) : '',
              DopravaKm: item.Doprava !== null && item.Doprava !== undefined ? String(Math.round(Number(item.Doprava) || 0)) : '',
              CisloProtokolu: item.CisloProtokolu !== null && item.CisloProtokolu !== undefined ? String(Math.round(Number(item.CisloProtokolu) || 0)) : '',
              ZpusobPlatbyId: item.ZpusobPlatbyId ? String(item.ZpusobPlatbyId) : '2',
              SkudceId: item.SkudceId ? String(item.SkudceId) : '10',
              StatusId: item.StatusId ? String(item.StatusId) : DEFAULT_NEW_JOB_STATUS_ID,
              FakturaStatusId: item.FakturaStatusId ? String(item.FakturaStatusId) : DEFAULT_FAKTURA_STATUS_ID,
            });
          } else {
            setForm({
              ZakazkaId: null,
              NazevZakazky: item.NazevZakazky || '',
              ZakaznikId: item.ZakaznikId || 0,
              Datum: '',
              ZamestnanecId: item.ZamestnanecId ? String(item.ZamestnanecId) : (defaultEmployeeId || ''),
              FirmaId: item.FirmaId ? String(item.FirmaId) : '1',
              Poznamka: item.Poznamka || item.Popis || '',
              DatumNaplanovano: '',
              CenaZakazky: item.CenaZakazky !== null && item.CenaZakazky !== undefined ? String(Math.round(Number(item.CenaZakazky) || 0)) : '',
              OstatniNaklady: item.OstatniNaklady !== null && item.OstatniNaklady !== undefined ? String(Math.round(Number(item.OstatniNaklady) || 0)) : '',
              DopravaKm: item.Doprava !== null && item.Doprava !== undefined ? String(Math.round(Number(item.Doprava) || 0)) : '',
              CisloProtokolu: '',
              ZpusobPlatbyId: item.ZpusobPlatbyId ? String(item.ZpusobPlatbyId) : '2',
              SkudceId: item.SkudceId ? String(item.SkudceId) : '10',
              StatusId: item.StatusId ? String(item.StatusId) : DEFAULT_NEW_JOB_STATUS_ID,
              FakturaStatusId: item.FakturaStatusId ? String(item.FakturaStatusId) : DEFAULT_FAKTURA_STATUS_ID,
            });
          }
        } else {
          setForm((prev) => ({
            ...prev,
            Datum: prev.Datum || todayIso(),
            FirmaId: prev.FirmaId || '1',
            ZamestnanecId: prev.ZamestnanecId || defaultEmployeeId || '',
            ZpusobPlatbyId: prev.ZpusobPlatbyId || '2',
            SkudceId: prev.SkudceId || '10',
            CisloProtokolu: prev.CisloProtokolu || '',
          }));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Načtení formuláře selhalo');
      }
    };

    run();
  }, [id, isEdit, isCopyMode, copyFromId, email, defaultEmployeeId]);

  useEffect(() => {
    if (!isMobile) return;

    setForm((prev) => ({
      ...prev,
      StatusId: DEFAULT_NEW_JOB_STATUS_ID,
      FakturaStatusId: DEFAULT_FAKTURA_STATUS_ID,
      Datum: prev.Datum || todayIso(),
      DatumNaplanovano: '',
      ZamestnanecId: prev.ZamestnanecId || defaultEmployeeId || '',
    }));
  }, [isMobile, defaultEmployeeId]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const text = [
        c.ZakaznikId,
        c.Nazev,
        c.Ulice,
        c.KontaktniOsoba,
        c.Email,
        c.Telefon,
        c.PravidelnyZakaznik ? 'pravidelny' : '',
      ].filter(Boolean).join(' | ').toLowerCase();
      return text.includes(q);
    });
  }, [customers, customerSearch]);

  const selectedCustomer = useMemo(() => {
    return customers.find((item) => Number(item.ZakaznikId) === Number(form.ZakaznikId)) || null;
  }, [customers, form.ZakaznikId]);

  const selectedEmployee = useMemo(() => {
    return ZAMESTNANCI.find((item) => item.id === String(form.ZamestnanecId)) || null;
  }, [form.ZamestnanecId]);

  const travelCost = useMemo(() => {
    const km = toNumberOrNull(form.DopravaKm) || 0;
    const rate = selectedEmployee ? selectedEmployee.kmRate : 0;
    return Math.round(km * rate);
  }, [form.DopravaKm, selectedEmployee]);

  const openPicker = () => {
    setPickerOpen(true);
    setPickerMode('list');
    setCustomerSearch('');
    setCustomerForm(emptyCustomer);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerMode('list');
  };

  const selectCustomer = (customer) => {
    setForm((prev) => ({
      ...prev,
      ZakaznikId: Number(customer.ZakaznikId),
      NazevZakazky: (customer.Nazev || prev.NazevZakazky || '').trim(),
    }));
    closePicker();
  };

  const saveCustomerAndSelect = async () => {
    setError('');
    setSavingCustomer(true);
    try {
      if (!customerForm.Nazev.trim()) throw new Error('Vyplňte název zákazníka.');

      const payload = {
        Nazev: customerForm.Nazev.trim(),
        Ulice: customerForm.Ulice.trim() || null,
        KontaktniOsoba: customerForm.KontaktniOsoba.trim() || null,
        Telefon: customerForm.Telefon.trim() || null,
      };

      const created = await createCustomer(payload);
      const refreshed = await fetchCustomers();
      setCustomers(refreshed);

      const selectedId = created && created.ZakaznikId ? created.ZakaznikId : 0;
      if (selectedId) {
        const createdCustomer = refreshed.find((c) => Number(c.ZakaznikId) === Number(selectedId));
        if (createdCustomer) {
          setForm((prev) => ({
            ...prev,
            ZakaznikId: selectedId,
            NazevZakazky: (createdCustomer.Nazev || prev.NazevZakazky || '').trim(),
          }));
        } else {
          setForm((prev) => ({ ...prev, ZakaznikId: selectedId }));
        }
      }

      setCustomerForm(emptyCustomer);
      closePicker();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Vytvoření zákazníka selhalo');
    } finally {
      setSavingCustomer(false);
    }
  };

  const applyAiDraft = (draft) => {
    if (draft?.SessionId) {
      setVoiceSaved(Boolean(draft.SavedJob));
      setForm((previous) => ({
        ...previous,
        ZakaznikId: draft.ZakaznikId || 0,
        NazevZakazky: draft.ZakaznikNazev || '',
        ...Object.fromEntries(['Datum', 'CenaZakazky', 'OstatniNaklady', 'DopravaKm', 'CisloProtokolu', 'Poznamka', 'SkudceId', 'FirmaId', 'ZpusobPlatbyId'].map((field) => [field, draft[field] == null ? '' : String(draft[field])])),
      }));
      setError('');
      return;
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (agentPanelOpen || voiceSaved || voicePending) return;
    setSaving(true);
    setError('');

    try {
      const useMobileDefaults = isMobile && !isEdit;
      const effectiveEmployeeId = useMobileDefaults ? (defaultEmployeeId || form.ZamestnanecId) : form.ZamestnanecId;
      const effectiveStatusId = useMobileDefaults ? DEFAULT_NEW_JOB_STATUS_ID : form.StatusId;
      const effectiveFakturaStatusId = useMobileDefaults ? DEFAULT_FAKTURA_STATUS_ID : form.FakturaStatusId;
      const effectivePlannedDate = useMobileDefaults ? '' : form.DatumNaplanovano;

      if (!form.ZakaznikId) throw new Error('Vyberte zákazníka.');
      if (!form.Datum) throw new Error('Vyplňte datum zakázky.');
      if (toNumberOrNull(form.DopravaKm) === null) throw new Error('Vyplňte dopravu v km.');
      if (!effectiveEmployeeId || Number(effectiveEmployeeId) <= 0) throw new Error('Vyplňte Zaměstnanec ID.');
      if (!form.FirmaId || Number(form.FirmaId) <= 0) throw new Error('Vyplňte Firma ID.');

      const customer = customers.find((item) => Number(item.ZakaznikId) === Number(form.ZakaznikId));
      const jobName = (customer?.Nazev || form.NazevZakazky || '').trim();
      if (!jobName) throw new Error('Zvolený zákazník nemá název.');

      const savedJobResponse = await saveJob({
        ZakazkaId: form.ZakazkaId,
        Datum: form.Datum,
        ZakaznikId: Number(form.ZakaznikId),
        ZamestnanecId: Number(effectiveEmployeeId),
        FirmaId: Number(form.FirmaId),
        NazevZakazky: jobName,
        ZpusobPlatbyId: Number(form.ZpusobPlatbyId) || null,
        SkudceId: Number(form.SkudceId) || null,
        CisloProtokolu: toIntegerOrNull(form.CisloProtokolu),
        Doprava: toIntegerOrNull(form.DopravaKm),
        CenaZakazky: toIntegerOrNull(form.CenaZakazky),
        OstatniNaklady: toIntegerOrNull(form.OstatniNaklady),
        Poznamka: form.Poznamka.trim() || null,
        Popis: form.Poznamka.trim() || null,
        Datum_naplanovano: effectivePlannedDate || null,
        StatusId: Number(effectiveStatusId) || Number(DEFAULT_NEW_JOB_STATUS_ID),
        FakturaStatusId: Number(effectiveFakturaStatusId) || Number(DEFAULT_FAKTURA_STATUS_ID),
        ChangedBy: email,
        CreatedBy: email,
        TechnikEmail: email,
      });

      const savedJob = buildSavedJobPreview({
        response: savedJobResponse,
        form,
        customer,
        effectiveEmployeeId,
        effectiveStatusId,
        effectivePlannedDate,
        email,
      });

      history.push({
        ...returnTo,
        state: {
          ...(returnTo.state || {}),
          savedJob,
        },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Uložení zakázky selhalo');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit || !form.ZakazkaId) return;
    if (!window.confirm(`Opravdu chcete smazat zakázku #${form.ZakazkaId}?`)) return;

    try {
      await deleteJobById(form.ZakazkaId);
      history.push(returnTo);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Smazání zakázky selhalo');
    }
  };

  const handleRealtimeAgentSaved = (state) => {
    const savedJobId = Number(state && state.SavedJob && state.SavedJob.ZakazkaId ? state.SavedJob.ZakazkaId : 0);
    const effectiveEmployeeId = defaultEmployeeId || form.ZamestnanecId;
    if (!savedJobId) {
      setError('Realtime agent nevrátil ID uložené zakázky.');
      return;
    }

    history.push({
      ...returnTo,
      state: {
        ...(returnTo.state || {}),
        savedJob: buildSavedJobPreviewFromDraft({
          response: { ZakazkaId: savedJobId },
          draft: state,
          effectiveEmployeeId,
          email,
        }),
      },
    });
  };

  return (
    <div>
      <h2 className="page-header">{isEdit ? `Editace zakázky #${id}` : (isCopyMode ? `Kopie zakázky #${copyFromId}` : 'Nová zakázka')}</h2>
      <div className="card">
        {error ? <p className="crm-error">{error}</p> : null}

        <form onSubmit={submit} className="form-grid crm-form-layout">
          <div className="crm-actions-row crm-actions-row--nav">
            <Link to={returnTo}>{returnLabel(returnTo)}</Link>
            {isEdit && <JobProtocolButton job={form} />}
            {!isEdit && !agentPanelOpen && !voiceSaved && <button type="button" className="btn btn-primary" onClick={() => {
              setVoiceSeed({ ...Object.fromEntries(['Datum', 'CenaZakazky', 'OstatniNaklady', 'DopravaKm', 'CisloProtokolu', 'Poznamka', 'FirmaId', 'ZpusobPlatbyId'].map((field) => [field, form[field]])), ZakaznikNazev: selectedCustomer?.Nazev || form.NazevZakazky, SkudceId: form.SkudceId === '10' ? undefined : form.SkudceId });
              setAgentPanelOpen(true);
            }}><i className="bx bx-microphone" aria-hidden="true" /> {voicePending ? 'Pokračovat hlasem' : 'Zadat hlasem'}</button>}
          </div>

          {!isEdit && agentPanelOpen && <RealtimeJobAgentPanel email={email} initialDraft={voiceSeed} isOpen onClose={() => {
            setAgentPanelOpen(false);
            try { setVoicePending(Boolean(sessionStorage.getItem(`crm:voice-session:${String(email || '').toLowerCase()}`))); } catch { setVoicePending(false); }
          }} onDraftUpdate={applyAiDraft} onSaved={handleRealtimeAgentSaved} />}
          <fieldset disabled={agentPanelOpen || voiceSaved || voicePending} style={{ display: 'contents', border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div className="crm-form-row crm-form-row--4">
            <div className="crm-form-field crm-form-field--span-3">
              <label>Zákazník</label>
              <div className="crm-customer-field">
                <input
                  value={selectedCustomer ? `${selectedCustomer.Nazev || 'Neznámý'}${selectedCustomer.Ulice ? `, ${selectedCustomer.Ulice}` : ''}` : ''}
                  placeholder="Není vybraný zákazník"
                  readOnly
                />
                <button type="button" className="btn btn-primary" onClick={openPicker}>Vybrat zákazníka</button>
              </div>
            </div>

            <div className="crm-form-field">
              <label>Datum zásahu</label>
              <input type="date" value={form.Datum} onChange={(e) => setForm((p) => ({ ...p, Datum: e.target.value }))} required />
            </div>
          </div>

          <div className="crm-form-row crm-form-row--4">
            <div className="crm-form-field">
              <label>Firma</label>
              <select value={form.FirmaId} onChange={(e) => setForm((p) => ({ ...p, FirmaId: e.target.value }))} required>
                {FIRMY.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>

            <div className="crm-form-field">
              <label>Škůdce</label>
              <select value={form.SkudceId} onChange={(e) => setForm((p) => ({ ...p, SkudceId: e.target.value }))}>
                {SKUDCI.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </div>

            {!isMobile ? (
            <div className="crm-form-field">
              <label>Technik</label>
              <select value={form.ZamestnanecId} onChange={(e) => setForm((p) => ({ ...p, ZamestnanecId: e.target.value }))} required>
                <option value="">Vyberte technika</option>
                {ZAMESTNANCI.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.fullName}</option>
                ))}
              </select>
            </div>
            ) : null}

            <div className="crm-form-field">
              <label>Číslo protokolu</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.CisloProtokolu}
                onChange={(e) => setForm((p) => ({ ...p, CisloProtokolu: e.target.value }))}
                onBlur={(e) => setForm((p) => ({ ...p, CisloProtokolu: normalizeWholeInputValue(e.target.value) }))}
              />
            </div>
          </div>

          <div className="crm-form-row crm-form-row--4">
            <div className="crm-form-field">
              <label>Cena zakázky</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.CenaZakazky}
                onChange={(e) => setForm((p) => ({ ...p, CenaZakazky: e.target.value }))}
                onBlur={(e) => setForm((p) => ({ ...p, CenaZakazky: normalizeWholeInputValue(e.target.value) }))}
              />
            </div>

            <div className="crm-form-field">
              <label>Způsob platby</label>
              <select value={form.ZpusobPlatbyId} onChange={(e) => setForm((p) => ({ ...p, ZpusobPlatbyId: e.target.value }))}>
                {ZPUSOBY_PLATBY.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </div>

            <div className="crm-form-field">
              <label>Doprava v km</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.DopravaKm}
                onChange={(e) => setForm((p) => ({ ...p, DopravaKm: e.target.value }))}
                onBlur={(e) => setForm((p) => ({ ...p, DopravaKm: normalizeWholeInputValue(e.target.value) }))}
              />
              <small className="crm-form-note">
                {`Doprava: ${travelCost.toLocaleString('cs-CZ')} Kč`}
              </small>
            </div>

            <div className="crm-form-field">
              <label>Ostatní náklady</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.OstatniNaklady}
                onChange={(e) => setForm((p) => ({ ...p, OstatniNaklady: e.target.value }))}
                onBlur={(e) => setForm((p) => ({ ...p, OstatniNaklady: normalizeWholeInputValue(e.target.value) }))}
              />
            </div>
          </div>

          {!isMobile ? (
            <div className="crm-form-field">
              <label>Poznámka</label>
              <textarea value={form.Poznamka} onChange={(e) => setForm((p) => ({ ...p, Poznamka: e.target.value }))} rows={4} />
            </div>
          ) : (
            <div className="crm-mobile-more">
              <button
                type="button"
                className="btn crm-mobile-more__toggle"
                aria-expanded={showMoreMobile}
                onClick={() => setShowMoreMobile((prev) => !prev)}
              >
                {showMoreMobile ? 'Méně' : 'Více'}
                <i className={`bx ${showMoreMobile ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
              </button>

              {showMoreMobile ? (
                <div className="crm-mobile-more__content">
                  <div className="crm-form-field">
                    <label>Poznámka</label>
                    <textarea value={form.Poznamka} onChange={(e) => setForm((p) => ({ ...p, Poznamka: e.target.value }))} rows={4} />
                  </div>
                  {isEdit ? <button className="btn btn-danger" type="button" onClick={remove}>Smazat</button> : null}
                </div>
              ) : null}
            </div>
          )}

          <div className="crm-actions-row crm-actions-row--form">
            <button className="btn btn-primary" type="submit" disabled={saving || agentPanelOpen || voiceSaved || voicePending}>{saving ? 'Ukládám...' : 'Uložit'}</button>
            {isEdit && !isMobile ? <button className="btn btn-danger" type="button" onClick={remove}>Smazat</button> : null}
          </div>
          </fieldset>
        </form>
      </div>

      {pickerOpen ? (
        <div className="crm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Výběr zákazníka">
          <div className="crm-modal-card">
            {pickerMode === 'list' ? (
              <>
                <div className="crm-modal-header">
                  <h3>Výběr zákazníka</h3>
                  <div className="crm-modal-header-actions">
                    <button type="button" className="btn btn-primary" onClick={() => setPickerMode('create')}>Vytvořit</button>
                    <button type="button" className="crm-modal-close-btn" aria-label="Zavřít" onClick={closePicker}>
                      <i className='bx bx-x'></i>
                    </button>
                  </div>
                </div>

                <input
                  className="crm-modal-search"
                  placeholder="Hledat zákazníka..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                />

                <div className="crm-customer-list">
                  {filteredCustomers.length ? filteredCustomers.map((customer) => (
                    <div key={customer.ZakaznikId} className="crm-customer-list__item">
                      <div>
                        <div className="crm-customer-name-row">
                          <strong>{customer.Nazev || 'Neznámý'}</strong>
                          {customer.PravidelnyZakaznik ? (
                            <span className="crm-regular-badge" title="Pravidelný zákazník" aria-label="Pravidelný zákazník">
                              <i className="bx bx-repeat"></i>
                              Pravidelný
                            </span>
                          ) : null}
                        </div>
                        <p>{customer.Ulice || '-'}</p>
                      </div>
                      <button type="button" className="btn btn-primary" onClick={() => selectCustomer(customer)}>Vybrat</button>
                    </div>
                  )) : <p>Žádný zákazník neodpovídá hledání.</p>}
                </div>
              </>
            ) : (
              <>
                <div className="crm-modal-header">
                  <h3>Nový zákazník</h3>
                  <button type="button" className="btn" onClick={() => setPickerMode('list')}>Zpět na výběr</button>
                </div>

                <div className="form-grid">
                  <label>Název zákazníka</label>
                  <input
                    value={customerForm.Nazev}
                    onChange={(e) => setCustomerForm((p) => ({ ...p, Nazev: e.target.value }))}
                    required
                  />

                  <label>Adresa</label>
                  <input
                    value={customerForm.Ulice}
                    onChange={(e) => setCustomerForm((p) => ({ ...p, Ulice: e.target.value }))}
                  />

                  <label>Kontaktní osoba</label>
                  <input
                    value={customerForm.KontaktniOsoba}
                    onChange={(e) => setCustomerForm((p) => ({ ...p, KontaktniOsoba: e.target.value }))}
                  />

                  <label>Telefon</label>
                  <input
                    value={customerForm.Telefon}
                    onChange={(e) => setCustomerForm((p) => ({ ...p, Telefon: e.target.value }))}
                  />

                  <div className="crm-actions-row">
                    <button type="button" className="btn btn-primary" onClick={saveCustomerAndSelect} disabled={savingCustomer}>
                      {savingCustomer ? 'Ukládám...' : 'Uložit'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ZakazkaForm;
